import { exteriorTotal } from "@/lib/exteriorTemplates";
import { softCostsTotal } from "@/lib/softCostsTemplates";

const RENO_PER_SQM = {
  light: 450,
  medium: 1100,
  heavy: 2200,
  full: 3600,
};

const FINISH_MULT = { budget: 0.7, mid: 1.0, premium: 1.6 };

export { RENO_PER_SQM, FINISH_MULT };

/**
 * Quick whole-property cost estimate from floor area × renovation type × finish.
 * Intended for the landing-page mini-estimator — matches the heuristic fallback
 * used inside the full app when no rooms/exterior/systems are modelled yet.
 */
export function quickRenovationEstimate({ floor_area_sqm, renovation_type = "medium", finish_level = "mid" }) {
  const sqm = Math.max(0, Number(floor_area_sqm) || 0);
  const rate = RENO_PER_SQM[renovation_type] ?? RENO_PER_SQM.medium;
  const fMult = FINISH_MULT[finish_level] ?? 1;
  return sqm * rate * fMult;
}

export function workItemTotal(item) {
  const q = Number(item?.quantity) || 0;
  const r = Number(item?.rate) || 0;
  return q * r;
}

export function roomTotals(room) {
  const items = room?.work_items || [];
  let total = 0, labour = 0, material = 0;
  items.forEach((it) => {
    const t = workItemTotal(it);
    total += t;
    if (it.kind === "labour") labour += t;
    else if (it.kind === "material") material += t;
    else { labour += t * 0.5; material += t * 0.5; }
  });
  return { total, labour, material };
}

export function projectRoomsTotal(project) {
  return (project?.rooms || []).reduce((sum, r) => sum + roomTotals(r).total, 0);
}

export function estimatedInteriorCost(project) {
  return projectRoomsTotal(project);
}

export function estimatedExteriorCost(project) {
  return exteriorTotal(project?.exterior || []);
}

export function estimatedSystemsCost(project) {
  return exteriorTotal(project?.systems || []);
}

// Sum of one extension's shell + fitout rooms.
export function extensionTotal(ext) {
  if (!ext) return 0;
  const shell = (ext.shell_items || []).reduce(
    (s, it) => s + (Number(it.quantity) || 0) * (Number(it.rate) || 0), 0
  );
  const fitout = (ext.fitout_rooms || []).reduce(
    (s, r) => s + roomTotals(r).total, 0
  );
  return shell + fitout;
}

export function estimatedExtensionsCost(project) {
  return (project?.extensions || []).reduce((s, e) => s + extensionTotal(e), 0);
}

// New sqm gained from extensions (knock-throughs contribute 0 by design).
export function extensionsAddedSqm(project) {
  return (project?.extensions || []).reduce(
    (s, e) => s + (Number(e?.footprint_sqm) || 0), 0
  );
}

// Total Gross Living Area after build = original floor area + new sqm.
export function projectGLA(project) {
  return (Number(project?.floor_area_sqm) || 0) + extensionsAddedSqm(project);
}

export function estimatedRenovationCost(project) {
  const modelled =
    estimatedInteriorCost(project) +
    estimatedExteriorCost(project) +
    estimatedSystemsCost(project) +
    estimatedExtensionsCost(project);
  if (modelled > 0) return modelled;
  const sqm = Number(project?.floor_area_sqm) || 0;
  const rate = RENO_PER_SQM[project?.renovation_type] ?? RENO_PER_SQM.medium;
  return sqm * rate;
}

export function estimatedSoftCosts(project) {
  const reno = estimatedRenovationCost(project);
  const items = project?.soft_costs || [];
  if (items.length > 0) return softCostsTotal(items, project, reno);
  // Heuristic fallback when soft-costs module hasn't been initialised
  const purchase = Number(project?.purchase_price) || 0;
  const sale = Number(project?.target_sale_price) || 0;
  return purchase * 0.03 + sale * 0.035;
}

export function totalProjectCost(project) {
  const purchase = Number(project?.purchase_price) || 0;
  return purchase + estimatedRenovationCost(project) + estimatedSoftCosts(project);
}

export function estimatedProfit(project) {
  const sale = Number(project?.target_sale_price) || 0;
  return sale - totalProjectCost(project);
}

export function estimatedROI(project) {
  const tc = totalProjectCost(project);
  if (tc <= 0) return 0;
  return (estimatedProfit(project) / tc) * 100;
}

export function margin(project) {
  const sale = Number(project?.target_sale_price) || 0;
  if (sale <= 0) return 0;
  return (estimatedProfit(project) / sale) * 100;
}

export function costPerSqm(project) {
  // Renovation cost per sqm — what users actually want to benchmark against
  // comparables. Excludes purchase price + soft costs. Uses post-build GLA
  // so extensions are counted in the denominator.
  const sqm = projectGLA(project);
  if (sqm <= 0) return 0;
  return estimatedRenovationCost(project) / sqm;
}

export function formatCurrency(n, currency = "USD") {
  const v = Number(n) || 0;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    // Fallback if Intl doesn't recognise the code
    return `${currency} ${Math.round(v).toLocaleString("en-US")}`;
  }
}

export function formatPct(n, digits = 1) {
  const v = Number(n) || 0;
  return `${v.toFixed(digits)}%`;
}
